
import { defineComponent, computed, toRef, ref, watch } from "vue";
import Editor from "@tinymce/tinymce-vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal } from "bootstrap";

import EvidenceListDrawer from "@/layout/extras/EvidenceListDrawer.vue";
import ApiService from "@/core/services/ApiService";
import UploadCustomImageEvidenceModal from "@/components/modals/general/UploadCustomImageEvidenceModal.vue";
import UploadCustomDocEvidenceModal from "@/components/modals/general/UploadCustomDocEvidenceModal.vue";
import GuideModal from "@/components/modals/general/GuideModal.vue";

export default defineComponent({
  name: "add-chargeback-response-drawer",
  emits: ["submit"],
  props: {
    templatesData: { type: Object, required: true },
    templateImages: { type: Array, required: true },
    incoming: { type: Object, required: true },
    reason: Object,
    reason_explained: Object,
  },
  components: {
    EvidenceListDrawer,
    Editor,
    UploadCustomImageEvidenceModal,
    UploadCustomDocEvidenceModal,
    GuideModal,
  },
  setup(props, { emit }) {
    const templates = toRef(props, "templatesData");
    const selectedTemplate = ref(0);
    const cbResponse = ref("");
    const editorApiKey = ref(process.env.VUE_APP_TINYMCE_API_KEY);
    const editorOption = ref({
      selector: "textarea",
      body_class: "editor_class",
      statusbar: false,
      height: "100%",
      content_style: [
        // ".editor_class { transform: scale(0.8, 0.8); transform-origin: top; } p {margin: 0;}",
        /*`
          body{
            padding:0px;
            margin: 0px auto 0px auto;
            font-family: "Noto Serif", serif; 
            font-size: 12px;
            color:#444;
          }
          p:not([class]) { display: none; }
          pre {white-space: pre-wrap;}
          h1, h2, h3 {#23282d;font-weight:600}
          h2 {font-size:1.6em}
          blockquote {margin: 0;
              box-shadow: inset 0px 0px 0px 0px #e2e4e7;
              border-left: 4px solid #000;
              padding-left: 1em;}
          #editable {padding:0px;}
          #editable:hover {outline:2px solid #e2e4e7}     
        `*/
        "p:not([class]) { display: none; }",
      ],
      content_css:
        "https://devcrm.chargebacks.online/images/php3IVFVq20220422100355.css",
      menubar: false,
      plugins: [
        "advlist autolink lists link image charmap print preview anchor",
        "searchreplace visualblocks code fullscreen",
        "insertdatetime media table paste code help wordcount",
      ],
      toolbar:
        "undo redo | cut copy paste pastetext | formatselect | code | bold italic backcolor | \
        link image | alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent | removeformat | visualblocks | help table tabledelete | \
        tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | \
        tableinsertcolbefore tableinsertcolafter tabledeleteco",
      /* we override default upload handler to simulate successful upload*/
      images_upload_handler: async function (blobInfo, success, failure) {
        // eslint-disable-next-line
        const formData: any = new FormData();
        formData.append("upload", blobInfo.blob(), blobInfo.filename());
        try {
          const res = await ApiService.post("imgupload", formData);
          if (res.data.success) {
            success(res.data.data.url);
          } else {
            failure(res.data.error.message);
          }
        } catch (e) {
          failure(
            e.response.data.error
              ? e.response.data.error.message
              : "Something went wrong!"
          );
        }
      },
    });
    const submitButtonRef = ref<null | HTMLButtonElement>(null);
    const selectedCustomEvidence = ref({});

    const onSelTemplate = (val) => {
      selectedTemplate.value = val;
      cbResponse.value = Object.values(templates.value)[selectedTemplate.value];
    };

    const onRemoveTemplate = async (val) => {
      const templateName = Object.keys(templates.value)[val];
      try {
        const res = await ApiService.put(
          `chargebacks/incoming/${props.incoming.id}?action=remove_evidence&evidence_name=${templateName}`,
          {}
        );
        if (res.data.success) {
          selectedTemplate.value = selectedTemplate.value - 1;
          if (selectedTemplate.value < 0) {
            selectedTemplate.value = 0;
          }
          cbResponse.value = Object.values(templates.value)[
            selectedTemplate.value
          ];

          onAddEvidence();
          Swal.fire({
            position: "bottom",
            text: "Evidence Library Page Successfully removed!",
            icon: "success",
            showConfirmButton: false,
            timer: 2000,
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }
    };

    const getTemplateName = computed(() => {
      if (Object.keys(templates.value).length !== 0) {
        const name = Object.keys(templates.value)[selectedTemplate.value];
        const nameAry = name.split("_");
        return `${nameAry[2]}_${nameAry[3]}`;
      }

      return "";
    });

    watch(
      () => templates.value,
      () => {
        cbResponse.value = Object.values(templates.value)[
          selectedTemplate.value
        ];
      }
    );

    const submit = async () => {
      // eslint-disable-next-line
      submitButtonRef.value!.disabled = true;
      // Activate indicator
      submitButtonRef.value?.setAttribute("data-kt-indicator", "on");

      // eslint-disable-next-line
      const payload: any = { html: cbResponse.value };
      try {
        const res = await ApiService.put(
          `chargebacks/incoming/${
            props.incoming.id
          }?action=update_response_packet&evidence_name=${
            Object.keys(templates.value)[selectedTemplate.value]
          }`,
          payload
        );
        if (res.data.success) {
          Swal.fire({
            text: "You have successfully updated the html!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(function () {
            // slider.value?.destroy();
            emit("submit");
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButtonRef.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButtonRef.value!.disabled = false;
    };

    const onAddEvidence = () => {
      // slider.value?.destroy();
      emit("submit");
    };

    const getAddedEvidences = computed(() => {
      return Object.keys(templates.value).map((name) => {
        const names = name.split("_");
        return names[1];
      });
    });

    const onAddCustom = (evidence) => {
      selectedCustomEvidence.value = { ...evidence };
      if (evidence.evidence_name === "Custom Image") {
        const modal = new Modal(
          document.getElementById("modal_upload_custom_image_evidence")
        );
        modal.show();
      } else {
        const modal = new Modal(
          document.getElementById("modal_upload_custom_doc_evidence")
        );
        modal.show();
      }
    };

    const drawer = ref(false);
    const onEdit = () => {
      drawer.value = true;
    };
    const onCloseEvidence = () => {
      drawer.value = false;
    };

    return {
      onSelTemplate,
      onRemoveTemplate,
      getTemplateName,
      cbResponse,
      editorOption,
      editorApiKey,
      submitButtonRef,
      submit,
      onAddEvidence,
      getAddedEvidences,
      onAddCustom,
      selectedCustomEvidence,

      onEdit,
      drawer,
      onCloseEvidence,
    };
  },
});
